/**
 * Casarto Model Selector
 * STABILE VERSION - Behebt den Flickity/Slider Crash beim Neuladen
 */

class ModelSelector {
  constructor() {
    this.data = null;
    this.selectedModel = null;
    this.selectedBrand = null;
    this.hasUserInteracted = false;
    this.imageCache = new Map();
    this.pendingTransitions = new Set();
    
    this.wrapper = document.querySelector('[data-model-selector]');
    if (this.wrapper) {
      this.trigger = this.wrapper.querySelector('[data-model-selector-trigger]');
      this.dropdown = this.wrapper.querySelector('[data-model-dropdown]');
      this.searchInput = this.wrapper.querySelector('[data-model-search]');
      this.modelList = this.wrapper.querySelector('[data-model-list]');
      this.clearButton = this.wrapper.querySelector('[data-model-clear]');
      this.label = this.wrapper.querySelector('[data-model-label]');
      
      this.mobileTrigger = this.wrapper.querySelector('[data-model-mobile-trigger]');
      this.modal = this.wrapper.querySelector('[data-model-modal]');
      this.modalOverlay = this.wrapper.querySelector('[data-modal-overlay]');
      this.modalClose = this.wrapper.querySelector('[data-modal-close]');
      this.searchInputMobile = this.wrapper.querySelector('[data-model-search-mobile]');
      this.modelListMobile = this.wrapper.querySelector('[data-model-list-mobile]');
      this.clearButtonMobile = this.wrapper.querySelector('[data-model-clear-mobile]');
    }
    
    this.drawerTrigger = document.querySelector('[data-model-selector-drawer-trigger]');
    this.drawerDropdown = document.querySelector('[data-model-dropdown-drawer]');
    this.drawerSearchInput = document.querySelector('[data-model-search-drawer]');
    this.drawerModelList = document.querySelector('[data-model-list-drawer]');
    this.drawerClearButton = document.querySelector('[data-model-clear-drawer]');
    this.drawerLabel = document.querySelector('[data-model-label-drawer]');
    
    this.sectionTrigger = document.querySelector('[data-model-selector-section-trigger]');
    this.sectionDropdown = document.querySelector('[data-model-dropdown-section]');
    this.sectionSearchInput = document.querySelector('[data-model-search-section]');
    this.sectionModelList = document.querySelector('[data-model-list-section]');
    this.sectionClearButton = document.querySelector('[data-model-clear-section]');
    this.sectionLabel = document.querySelector('[data-model-label-section]');
    
    this.init();
  }

  getSelectionLabel() { return [this.selectedBrand, this.selectedModel].filter(Boolean).join(' '); }
  isMobileViewport() { return window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : window.innerWidth < 750; }
  
  init() {
    this.loadData();
    this.buildModelSlugSet();
    this.loadSavedModel();
    this.attachEventListeners();
    this.updateUI();
    
    // Wir führen den Filter sofort aus. Dadurch werden falsche Bilder versteckt, 
    // noch BEVOR das Theme den Slider baut.
    this.applyModelToPage();
    
    // Für Theme-interne Navigation (AJAX/Turbo)
    document.addEventListener('page:loaded', () => {
      this.applyModelToPage();
    });
  }
  
  loadData() {
    const dataScript = document.querySelector('[data-model-selector-data]');
    if (dataScript) {
      try { this.data = JSON.parse(dataScript.textContent); } 
      catch (e) { console.error('Model Selector: Failed to parse data', e); }
    }
  }

  buildModelSlugSet() {
    this.modelSlugSet = new Set();
    if (!this.data || !this.data.models) return;
    Object.values(this.data.models).forEach(models => {
      models.forEach(m => this.modelSlugSet.add(this.normalizeModelName(m).toLowerCase()));
    });
  }

  isKnownModelIdentifierFromText(text) {
    if (!text) return false;
    const re = /_([A-Za-z0-9-]+)_/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      const slug = m[1].toLowerCase();
      if (this.modelSlugSet && this.modelSlugSet.has(slug)) return true;
    }
    return false;
  }
  
  loadSavedModel() {
    const saved = this.getCookie(this.data?.cookieName);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.selectedBrand = parsed.brand;
        this.selectedModel = parsed.model;
        this.hasUserInteracted = true;
      } catch (e) {}
    } else {
      this.selectedBrand = 'Apple';
      this.selectedModel = 'iPhone 17 Pro';
      this.hasUserInteracted = false;
    }
  }
  
  attachEventListeners() {
    if (this.trigger) this.trigger.addEventListener('click', () => this.toggleDropdown());
    if (this.searchInput) this.searchInput.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.modelList), 200));
    if (this.clearButton) this.clearButton.addEventListener('click', () => this.clearSelection());
    if (this.mobileTrigger) this.mobileTrigger.addEventListener('click', () => this.openModal());
    if (this.modalOverlay) this.modalOverlay.addEventListener('click', () => this.closeModal());
    if (this.modalClose) this.modalClose.addEventListener('click', () => this.closeModal());
    if (this.searchInputMobile) this.searchInputMobile.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.modelListMobile), 200));
    if (this.clearButtonMobile) this.clearButtonMobile.addEventListener('click', () => { this.clearSelection(); this.closeModal(); });
    
    if (this.drawerTrigger) this.drawerTrigger.addEventListener('click', () => this.toggleDrawerDropdown());
    if (this.drawerSearchInput) this.drawerSearchInput.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.drawerModelList), 200));
    if (this.drawerClearButton) this.drawerClearButton.addEventListener('click', () => { this.clearSelection(); this.closeDrawerDropdown(); });
    
    if (this.sectionTrigger) this.sectionTrigger.addEventListener('click', () => this.toggleSectionDropdown());
    if (this.sectionSearchInput) this.sectionSearchInput.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.sectionModelList), 200));
    if (this.sectionClearButton) this.sectionClearButton.addEventListener('click', () => { this.clearSelection(); this.closeSectionDropdown(); });
    
    document.addEventListener('click', (e) => {
      if (this.wrapper && !this.wrapper.contains(e.target)) this.closeDropdown();
      const drawerSelector = document.querySelector('.model-selector--drawer');
      if (drawerSelector && !drawerSelector.contains(e.target)) this.closeDrawerDropdown();
      const sectionSelector = document.querySelector('.model-selector-section__search-container');
      if (sectionSelector && !sectionSelector.contains(e.target)) this.closeSectionDropdown();
    });
    
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) this.syncWithVariantPicker(e.detail.variant);
    });
  }
  
  toggleDropdown() { this.dropdown.hidden ? this.openDropdown() : this.closeDropdown(); }
  openDropdown() { this.renderModelList(this.modelList); this.dropdown.hidden = false; this.searchInput && this.searchInput.focus(); }
  closeDropdown() { if (!this.dropdown) return; this.dropdown.hidden = true; if (this.searchInput) this.searchInput.value = ''; }
  openModal() { this.renderModelList(this.modelListMobile); if (!this.modal) return; this.modal.hidden = false; document.body.style.overflow = 'hidden'; setTimeout(() => { this.searchInputMobile && this.searchInputMobile.focus(); }, 300); }
  closeModal() { if (!this.modal) return; this.modal.hidden = true; document.body.style.overflow = ''; if (this.searchInputMobile) this.searchInputMobile.value = ''; }
  toggleDrawerDropdown() { if (!this.drawerDropdown) return; this.drawerDropdown.hidden ? this.openDrawerDropdown() : this.closeDrawerDropdown(); }
  openDrawerDropdown() { this.renderModelList(this.drawerModelList); if (!this.drawerDropdown) return; this.drawerDropdown.hidden = false; this.drawerSearchInput && this.drawerSearchInput.focus(); }
  closeDrawerDropdown() { if (!this.drawerDropdown) return; this.drawerDropdown.hidden = true; if (this.drawerSearchInput) this.drawerSearchInput.value = ''; }
  toggleSectionDropdown() { if (!this.sectionDropdown) return; this.sectionDropdown.hidden ? this.openSectionDropdown() : this.closeSectionDropdown(); }
  openSectionDropdown() { this.renderModelList(this.sectionModelList); if (!this.sectionDropdown) return; this.sectionDropdown.hidden = false; this.sectionSearchInput && this.sectionSearchInput.focus(); }
  closeSectionDropdown() { if (!this.sectionDropdown) return; this.sectionDropdown.hidden = true; if (this.sectionSearchInput) this.sectionSearchInput.value = ''; }
  
  renderModelList(container, filter = '') {
    if (!container || !this.data) return;
    const filterLower = filter.toLowerCase();
    let html = '';
    
    Object.entries(this.data.models).forEach(([brand, models]) => {
      const filteredModels = filter ? models.filter(m => m.toLowerCase().includes(filterLower)) : models;
      if (filteredModels.length === 0) return;
      html += `<div class="model-selector__group"><div class="model-selector__group-title">${brand}</div><div class="model-selector__group-items">${filteredModels.map(model => `<button type="button" class="model-selector__item${this.selectedModel === model ? ' model-selector__item--active' : ''}" data-brand="${brand}" data-model="${model}">${model}</button>`).join('')}</div></div>`;
    });
    
    if (html === '') html = '<div class="model-selector__no-results">Keine Modelle gefunden</div>';
    container.innerHTML = html;
    
    container.querySelectorAll('[data-model]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.selectModel(e.currentTarget.dataset.brand, e.currentTarget.dataset.model);
      });
    });
  }
  
  filterModels(query, container) { this.renderModelList(container, query); }
  
  selectModel(brand, model) {
    this.selectedBrand = brand; this.selectedModel = model; this.hasUserInteracted = true;
    this.setCookie(this.data.cookieName, JSON.stringify({ brand, model }), this.data.cookieExpiry);
    this.updateUI(); this.closeDropdown(); this.closeModal(); this.closeDrawerDropdown(); this.closeSectionDropdown();
    this.applyModelToPage();
    this.scrollToGallery();
    document.dispatchEvent(new CustomEvent('model:selected', { detail: { brand, model } }));
  }
  
  clearSelection() {
    this.selectedBrand = 'Apple'; this.selectedModel = 'iPhone 17 Pro'; this.hasUserInteracted = false;
    this.deleteCookie(this.data.cookieName); this.updateUI(); this.applyModelToPage();
    document.dispatchEvent(new CustomEvent('model:cleared'));
  }
  
  updateUI() {
    if (this.label) this.label.textContent = this.hasUserInteracted ? this.getSelectionLabel() : 'z.B. iPhone 17, Galaxy S25 ...';
    if (this.mobileTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.mobileTrigger.classList.add('model-selector__mobile-trigger--active');
        this.mobileTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.mobileTrigger.classList.remove('model-selector__mobile-trigger--active');
        this.mobileTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
    if (this.drawerLabel) this.drawerLabel.textContent = this.hasUserInteracted ? this.getSelectionLabel() : 'z.B. iPhone 17, Galaxy S25 ...';
    if (this.drawerTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.drawerTrigger.classList.add('model-selector__trigger--has-selection');
        this.drawerTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.drawerTrigger.classList.remove('model-selector__trigger--has-selection');
        this.drawerTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
    if (this.sectionLabel && this.hasUserInteracted) this.sectionLabel.textContent = this.getSelectionLabel();
    if (this.sectionTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.sectionTrigger.classList.add('model-selector-section__trigger--has-selection');
        this.sectionTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.sectionTrigger.classList.remove('model-selector-section__trigger--has-selection');
        this.sectionTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
  }
  
  applyModelToPage() {
    const modelSlug = this.normalizeModelName(this.selectedModel);
    this.updateVariantPicker();
    this.filterProductImages(modelSlug);
    this.updateCollectionGrid(modelSlug);
  }
  
  filterProductImages(modelSlug) {
    const modelSlugLower = (modelSlug || '').toLowerCase();
    
    const slideshow = document.querySelector('.product-slideshow');
    const allSlides = document.querySelectorAll('.product-main-slide');
    const thumbs = document.querySelectorAll('[data-product-thumbs] .product__thumb-item');
    
    if (!slideshow || allSlides.length === 0) return;
    
    let hasVisibleSlides = false;
    
    // 1. Bilder markieren
    allSlides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const src = (img.src || img.dataset.src || '').toLowerCase();
      const alt = (img.alt || '').toLowerCase();
      const containsModel = src.includes(`_${modelSlugLower}_`) || alt.includes(`_${modelSlugLower}_`);
      const hasKnownModelIdentifier = this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);
      
      if (hasKnownModelIdentifier && !containsModel) {
        slide.classList.add('hide'); 
      } else {
        slide.classList.remove('hide');
        hasVisibleSlides = true;
      }
    });
    
    thumbs.forEach(thumb => {
      const img = thumb.querySelector('img');
      if (!img) return;
      const src = (img.src || img.dataset.src || '').toLowerCase();
      const alt = (img.alt || '').toLowerCase();
      const containsModel = src.includes(`_${modelSlugLower}_`) || alt.includes(`_${modelSlugLower}_`);
      const hasKnownModelIdentifier = this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);
      
      if (hasKnownModelIdentifier && !containsModel) {
        thumb.classList.add('hide');
      } else {
        thumb.classList.remove('hide');
      }
    });

    // 2. LOGIK-WECHSEL: Slider nur neu bauen, wenn er bereits existiert!
    // Auf F5 existiert er noch nicht (flickity-enabled fehlt). Das Theme baut ihn gleich automatisch.
    if (hasVisibleSlides) {
      const isFlickityActive = slideshow.classList.contains('flickity-enabled');
      
      if (isFlickityActive) {
        // Slider war schon da (z.B. durch Klick auf Modell). Wir bauen ihn sauber neu.
        this.rebuildThemeSlider(allSlides, slideshow);
      }
      // Wenn isFlickityActive FALSE ist, tun wir nichts. Das Theme macht den Rest!
    }
  }

  // Funktion in eine eigene Methode ausgelagert
  rebuildThemeSlider(allSlides, slideshowContainer) {
    if (window.theme && window.theme.sections && window.theme.sections.instances) {
      const productInstance = window.theme.sections.instances.find(inst => inst.type === 'product');
      if (productInstance && typeof productInstance.initProductSlider === 'function') {
          
          // Höhe einfrieren
          const currentHeight = slideshowContainer.offsetHeight;
          if (currentHeight > 0) {
            slideshowContainer.style.minHeight = `${currentHeight}px`;
          }

          // Ersten sichtbaren Slide finden
          const firstVisibleSlide = Array.from(allSlides).find(s => !s.classList.contains('hide'));
          if(firstVisibleSlide) {
              productInstance.settings.currentSlideIndex = parseInt(firstVisibleSlide.getAttribute('data-index') || 0); 
          } else {
              productInstance.settings.currentSlideIndex = 0; 
          }
          
          // Slider neu bauen
          productInstance.initProductSlider();

          // Höhe wieder freigeben
          setTimeout(() => {
            slideshowContainer.style.minHeight = '';
          }, 400); 
      }
    }
  }
  
  updateCollectionGrid(modelSlug) {
    const gridItems = document.querySelectorAll('.grid-product[data-product-handle]');
    gridItems.forEach(item => {
      const productHandle = item.dataset.productHandle;
      if (productHandle) this.updateGridItemImage(item, modelSlug);
    });
  }
  
  updateGridItemImage(gridItem, modelSlug) {
    const productId = gridItem.dataset.productId;
    if (!productId) return;
    const img = gridItem.querySelector('.grid-product__main-image');
    if (!img) return;
    const transitionKey = `${productId}-${modelSlug}`;
    if (this.pendingTransitions.has(transitionKey)) return;
    
    fetch(`/products/${gridItem.dataset.productHandle}.js`)
      .then(res => res.json())
      .then(product => {
        const matchingVariant = product.variants.find(v => {
          const option2 = v.option2;
          return option2 && this.normalizeModelName(option2) === modelSlug;
        });
        
        if (matchingVariant && matchingVariant.featured_image) {
          const rawImageSrc = matchingVariant.featured_image.src;
          const newImageSrc = this.getShopifyImageUrl(rawImageSrc, 540);
          if (img.src === newImageSrc) return;
          this.pendingTransitions.add(transitionKey);
          
          this.preloadImage(newImageSrc).then(preloadedImg => {
            const imageWrapper = img.parentElement;
            const existingOverlay = imageWrapper.querySelector('.grid-product__transition-overlay');
            if (existingOverlay) existingOverlay.remove();
            
            const overlayImg = document.createElement('img');
            overlayImg.className = img.className + ' grid-product__transition-overlay';
            overlayImg.src = newImageSrc;
            overlayImg.srcset = this.generateSrcset(newImageSrc);
            overlayImg.alt = img.alt;
            
            Object.assign(overlayImg.style, {
              position: 'absolute', top: '0', left: '0', width: '100%', height: '100%',
              opacity: '0', zIndex: '2', pointerEvents: 'none', willChange: 'opacity'
            });
            
            const wrapperPosition = window.getComputedStyle(imageWrapper).position;
            if (wrapperPosition === 'static') imageWrapper.style.position = 'relative';
            
            imageWrapper.appendChild(overlayImg);
            
            if (overlayImg.complete) this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
            else {
              overlayImg.onload = () => this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
              setTimeout(() => { if (overlayImg.parentElement) this.startCrossFade(overlayImg, img, newImageSrc, transitionKey); }, 1000);
            }
          }).catch(err => {
            img.src = newImageSrc; img.srcset = this.generateSrcset(newImageSrc);
            this.pendingTransitions.delete(transitionKey);
          });
        }
      }).catch(err => this.pendingTransitions.delete(transitionKey));
  }
  
  startCrossFade(overlayImg, originalImg, newImageSrc, transitionKey) {
    overlayImg.offsetHeight;
    requestAnimationFrame(() => { overlayImg.style.transition = 'opacity 0.4s ease-out'; overlayImg.style.opacity = '1'; });
    setTimeout(() => {
      originalImg.src = newImageSrc; originalImg.srcset = this.generateSrcset(newImageSrc);
      if (overlayImg.parentElement) overlayImg.remove();
      this.pendingTransitions.delete(transitionKey);
    }, 400);
  }
  
  preloadImage(src) {
    if (this.imageCache.has(src)) return Promise.resolve(this.imageCache.get(src));
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { this.imageCache.set(src, img); resolve(img); };
      img.onerror = reject; img.src = src;
    });
  }
  
  updateVariantPicker() {
    this._updatingVariantPicker = true;
    const brandContainer = document.querySelector('[data-index="option1"]');
    const modelContainer = document.querySelector('[data-index="option2"]');
    if (!brandContainer || !modelContainer) { this._updatingVariantPicker = false; return; }
    
    const brandSelect = brandContainer.querySelector('select');
    if (brandSelect) {
      if (this.hasUserInteracted) {
        if (brandSelect.value !== this.selectedBrand) {
          brandSelect.value = this.selectedBrand;
          brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else brandSelect.value = '';
    } else {
      const brandInputs = brandContainer.querySelectorAll('input[data-variant-input]');
      brandInputs.forEach(input => {
        if (this.hasUserInteracted && input.value === this.selectedBrand) {
          if (!input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
        } else if (!this.hasUserInteracted) input.checked = false;
      });
    }
    
    setTimeout(() => {
      const modelSelect = modelContainer.querySelector('select');
      if (modelSelect) {
        if (this.hasUserInteracted) {
          if (modelSelect.disabled) modelSelect.disabled = false;
          const modelOption = Array.from(modelSelect.options).find(o => o.value === this.selectedModel);
          if (modelOption && modelSelect.value !== this.selectedModel) {
            modelSelect.value = this.selectedModel;
            modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else { modelSelect.value = ''; modelSelect.disabled = true; }
      } else {
        const modelInputs = modelContainer.querySelectorAll('input[data-variant-input]');
        modelInputs.forEach(input => {
          if (this.hasUserInteracted && input.value === this.selectedModel) {
            if (!input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
          } else if (!this.hasUserInteracted) { input.checked = false; input.disabled = true; }
        });
      }
      if (this.hasUserInteracted) { this.triggerShopifyVariantUpdate(); }
      this._updatingVariantPicker = false;
    }, 200);
  }

  triggerShopifyVariantUpdate() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const variantIdInput = form.querySelector('select[name="id"]');
    if (variantIdInput) variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    const shopifySelect = document.querySelector('[name="id"]');
    if (shopifySelect) shopifySelect.dispatchEvent(new Event('change', { bubbles: true }));
  }
  
  syncWithVariantPicker(variant) {
    if (this._updatingVariantPicker) return;
    if (variant.option1 && variant.option2) {
      this.hasUserInteracted = true;
      if (this.selectedBrand !== variant.option1 || this.selectedModel !== variant.option2) {
        this._syncingFromVariantPicker = true;
        this.selectedBrand = variant.option1;
        this.selectedModel = variant.option2;
        this.setCookie(this.data.cookieName, JSON.stringify({ brand: this.selectedBrand, model: this.selectedModel }), this.data.cookieExpiry);
        this.updateUI(); 
        this.applyModelToPage();
        this.scrollToGallery(); // Auto-Scroll bei Änderung
        
        document.dispatchEvent(new CustomEvent('model:selected', { detail: { brand: this.selectedBrand, model: this.selectedModel } }));
        this._syncingFromVariantPicker = false;
      }
    }
  }

  scrollToGallery() {
    setTimeout(() => {
      const gallery = document.querySelector('[data-product-images]') || document.querySelector('.product__photos');
      if (!gallery) return;

      const header = document.querySelector('.site-header') || document.querySelector('header-wrapper') || document.querySelector('header');
      const headerHeight = header ? header.offsetHeight : 0;
      
      const rect = gallery.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      const targetTop = rect.top + scrollTop - headerHeight - 20;

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    }, 150);
  }
  
  normalizeModelName(model) { return model.replace(/\s+/g, '-').replace(/\+/g, '-Plus').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }
  getShopifyImageUrl(src, width) { if (!src) return ''; let c = src.replace(/&width=\d+/g, ''); return `${c}${c.includes('?') ? '&' : '?'}width=${width}`; }
  generateSrcset(src) { const widths = [160, 240, 360, 420, 540]; return widths.map(w => `${this.getShopifyImageUrl(src, w)} ${w}w`).join(', '); }
  debounce(func, wait) { let timeout; return function (...args) { clearTimeout(timeout); timeout = setTimeout(() => func.apply(this, args), wait); }; }
  setCookie(n, v, d) { const e = new Date(); e.setTime(e.getTime() + d * 24 * 60 * 60 * 1000); document.cookie = `${n}=${v};expires=${e.toUTCString()};path=/;SameSite=Lax`; }
  getCookie(n) { const nameEQ = n + '='; const ca = document.cookie.split(';'); for (let i = 0; i < ca.length; i++) { let c = ca[i]; while (c.charAt(0) === ' ') c = c.substring(1, c.length); if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length); } return null; }
  deleteCookie(n) { document.cookie = `${n}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`; }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.modelSelectorInstance = new ModelSelector());
else window.modelSelectorInstance = new ModelSelector();
window.CasartoModelSelector = ModelSelector;